import React, { useState, useEffect } from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { useAuthActions } from '../lib/actions';
import { AuthError } from '../lib/slice';
import redirectTo from '../../../utils/redirect';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  isLoading?: boolean;
  error?: AuthError | null;
  defaultEmail?: string;
  onSuccess?: () => void;
  title?: string;
  subtitle?: string;
  skipRedirect?: boolean; // Si se debe omitir la redirección después del login
  redirectGoogleUri?: string; // URI de redirección para Google
}

interface FormErrors {
  email?: string;
  password?: string;
  confirmPassword?: string;
}

export const AuthModal: React.FC<AuthModalProps> = ({ 
  isOpen, 
  onClose, 
  isLoading, 
  error,
  defaultEmail = '',
  onSuccess,
  title,
  subtitle,
  skipRedirect = false,
  redirectGoogleUri = undefined
}) => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState(defaultEmail);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  
  const { signIn, signUp, signInWithGoogle, clearError } = useAuthActions();

  // Update email when defaultEmail changes
  useEffect(() => {
    if (defaultEmail) {
      setEmail(defaultEmail);
    }
  }, [defaultEmail]);

  if (!isOpen) return null;

  const validateEmail = (email: string): string | undefined => {
    if (!email) return 'Email is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return 'Please enter a valid email address';
    return undefined;
  };

  const validatePassword = (password: string): string | undefined => {
    if (!password) return 'Password is required';
    if (password.length < 8) return 'Password must be at least 8 characters';
    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
      return 'Password must contain at least one uppercase letter, one lowercase letter, and one number';
    }
    return undefined;
  };

  const validateConfirmPassword = (password: string, confirmPassword: string): string | undefined => {
    if (!confirmPassword) return 'Please confirm your password';
    if (password !== confirmPassword) return 'Passwords do not match';
    return undefined;
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Validate email
    const emailError = validateEmail(email);
    if (emailError) newErrors.email = emailError;

    // Validate password
    const passwordError = validatePassword(password);
    if (passwordError) newErrors.password = passwordError;

    // Validate confirm password (only for signup)
    if (!isLogin) {
      const confirmPasswordError = validateConfirmPassword(password, confirmPassword);
      if (confirmPasswordError) newErrors.confirmPassword = confirmPasswordError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    // Clear previous errors
    clearError();

    try {
      if (isLogin) {
        const response = await signIn({ email, password });
        if (response.success) {
          onSuccess?.();
          if(!skipRedirect) {
            redirectTo('/dashboard'); // Navigate to dashboard after successful login
          }
        }
      } else {
        await signUp({ email, password });
        // For signup, user needs to confirm email first
        onSuccess?.();
      }
    } catch (error: any) {
      // Handle errors here if needed
    }
  };

  const getRedirectUri = () => {
    // Si hay un redirectGoogleUri personalizado, úsalo
    if (redirectGoogleUri) {
      return 'https://cap.com/auth/callback?redirect_to=' + encodeURIComponent(redirectGoogleUri);
    }
    
    const isDevelopment = window.location.hostname === 'localhost';
    return isDevelopment
      ? 'http://localhost:3000/auth/callback'
      : 'https://cap.com/auth/callback';
  };

  const handleGoogleAuth = async () => {
    try {
      clearError();
      const redirectUri = getRedirectUri();
      // For Google auth, the redirect will be handled by the callback page
      await signInWithGoogle(redirectUri);
      // Don't call onSuccess here, let the callback handle the redirect
    } catch (error: any) {
      // Handle errors here if needed
    }
  };

  const resetForm = () => {
    setPassword('');
    setConfirmPassword('');
    setErrors({});
    clearError();
  };

  const handleModeChange = () => {
    resetForm();
    setIsLogin(!isLogin);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-8 w-full max-w-md mx-4 relative">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          disabled={isLoading}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {title || (isLogin ? 'Bienvenido de nuevo' : 'Crear cuenta')}
          </h2>
          <p className="text-gray-600">
            {subtitle || (isLogin ? 'Iniciá sesión en tu cuenta' : 'Unite a Punta Tech Meetup hoy')}
          </p>
        </div>

        {/* Error Message Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  {error.message}
                </h3>
              </div>
              {/* Botón para cerrar el error */}
              <div className="ml-auto pl-3">
                <button
                  onClick={clearError}
                  className="inline-flex rounded-md bg-red-50 p-1.5 text-red-500 hover:bg-red-100"
                >
                  <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Google Auth Button */}
        <Button
          onClick={handleGoogleAuth}
          variant="outline"
          className="w-full mb-6 h-12 border-gray-300"
          disabled={isLoading}
        >
          {isLoading ? (
            <div className="w-5 h-5 mr-3 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600"></div>
          ) : (
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
          )}
          Continuar con Google
        </Button>

        {/* Divider */}
        <div className="relative mb-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">o</span>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Input
              type="email"
              placeholder="Correo electrónico"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (errors.email) {
                  setErrors(prev => ({ ...prev, email: undefined }));
                }
                // Limpiar error global al escribir
                if (error) {
                  clearError();
                }
              }}
              disabled={!!defaultEmail || isLoading}
              className={`w-full h-12 ${errors.email ? 'border-red-500' : ''} ${defaultEmail ? 'bg-gray-50' : ''}`}
              required
            />
            {errors.email && (
              <p className="text-red-500 text-sm mt-1">{errors.email}</p>
            )}
            {defaultEmail && (
              <p className="text-blue-600 text-sm mt-1">
                Usa este correo para aceptar la invitación
              </p>
            )}
          </div>

          <div>
            <Input
              type="password"
              placeholder="Contraseña"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (errors.password) {
                  setErrors(prev => ({ ...prev, password: undefined }));
                }
                // Limpiar error global al escribir
                if (error) {
                  clearError();
                }
              }}
              className={`w-full h-12 ${errors.password ? 'border-red-500' : ''}`}
              disabled={isLoading}
              required
            />
            {errors.password && (
              <p className="text-red-500 text-sm mt-1">{errors.password}</p>
            )}
          </div>

          {!isLogin && (
            <div>
              <Input
                type="password"
                placeholder="Confirmar contraseña"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (errors.confirmPassword) {
                    setErrors(prev => ({ ...prev, confirmPassword: undefined }));
                  }
                }}
                className={`w-full h-12 ${errors.confirmPassword ? 'border-red-500' : ''}`}
                disabled={isLoading}
                required
              />
              {errors.confirmPassword && (
                <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>
              )}
            </div>
          )}

          <Button 
            type="submit" 
            className="w-full h-12 bg-accent text-white hover:bg-accent/90 disabled:opacity-50"
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="flex items-center">
                <div className="w-5 h-5 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                {isLogin ? 'Iniciando sesión...' : 'Creando cuenta...'}
              </div>
            ) : (
              isLogin ? 'Iniciar sesión' : 'Crear cuenta'
            )}
          </Button>
        </form>

        {/* Toggle between login/signup */}
        <div className="text-center mt-6">
          <p className="text-gray-600">
            {isLogin ? "¿No tienes una cuenta? " : "¿Ya tienes una cuenta? "}
            <button
              onClick={handleModeChange}
              className="text-accent font-medium hover:underline"
              disabled={isLoading}
            >
              {isLogin ? 'Regístrate' : 'Inicia sesión'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};